import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit';
import axios from 'axios';
import { datadogLogs } from '@datadog/browser-logs';
import { ENV_CONFIG } from 'config/environment';
import { getMessageFromAxiosError } from 'helpers/errors';
import API_ROUTES from 'services/apiRoutes';
import { getSiteAccessToken } from 'services/storage';
import { initialState } from './initialState';
import type { ContributionSummary, ContributionSummaryState } from './types';

const headers = {
  'Content-Type': 'application/json',
  'Access-Control-Allow-Origin': '*',
};

// Backend caps recipient_ids at 200 (422 otherwise). 150 also keeps the query
// string well under proxy/CDN URL limits. Longer slates lose banners past the cap.
export const MAX_RECIPIENT_IDS = 150;

// Slate mode: one summary per requested recipient. Passing no ids to the
// endpoint means "all-recipients" (the profile view), so an
// empty list short-circuits here rather than accidentally triggering it.
export const getContributionSummaries = createAsyncThunk<
  ContributionSummary[],
  string[],
  { rejectValue: string }
>('contributionSummary/get', async (recipientIds, { rejectWithValue }) => {
  if (!recipientIds.length) return [];

  const cappedIds = recipientIds.slice(0, MAX_RECIPIENT_IDS);
  if (cappedIds.length < recipientIds.length) {
    datadogLogs.logger.warn('Contribution summary recipient_ids truncated', {
      requested: recipientIds.length,
      sent: cappedIds.length,
    });
  }

  const query = cappedIds.map(id => `recipient_ids=${encodeURIComponent(id)}`).join('&');
  const url = `${ENV_CONFIG().API.BASE_URL}${API_ROUTES.GET_CONTRIBUTION_SUMMARIES}?${query}`;

  try {
    const response = await axios.get(url, {
      headers: { ...headers, Authorization: `Bearer ${getSiteAccessToken()}` },
    });
    return response.data.summaries as ContributionSummary[];
  } catch (error: any) {
    datadogLogs.logger.warn(
      'Error occurred fetching contribution summaries',
      {},
      new Error(getMessageFromAxiosError(error))
    );
    return rejectWithValue(error.message);
  }
});

// All-recipeints mode: every recipient the donor has given to this cycle (no
// recipient_ids param), for the profile "This Cycle" view. The backend returns
// them ordered (amount desc, then name asc). We replay that order via
// orderedRecipientIds rather than re-sorting client-side.
export const getAllContributionSummaries = createAsyncThunk<
  ContributionSummary[],
  void,
  { rejectValue: string }
>('contributionSummary/getAll', async (_, { rejectWithValue }) => {
  const url = `${ENV_CONFIG().API.BASE_URL}${API_ROUTES.GET_CONTRIBUTION_SUMMARIES}`;

  try {
    const response = await axios.get(url, {
      headers: { ...headers, Authorization: `Bearer ${getSiteAccessToken()}` },
    });
    return response.data.summaries as ContributionSummary[];
  } catch (error: any) {
    datadogLogs.logger.warn(
      'Error occurred fetching all contribution summaries',
      {},
      new Error(getMessageFromAxiosError(error))
    );
    return rejectWithValue(error.message);
  }
});

const laterDate = (a: string | null, b: string | null): string | null => {
  if (!a) return b;
  if (!b) return a;
  return new Date(a) > new Date(b) ? a : b;
};

// Merges a batch into the keyed map, combining same-recipient_id rows within
// the batch rather than into the existing store value — the slate fetch's
// row is already a complete total, so adding to it would double-count.
const mergeSummaries = (state: ContributionSummaryState, summaries: ContributionSummary[]) => {
  const combined = new Map<string, ContributionSummary>();
  for (const summary of summaries) {
    const existing = combined.get(summary.recipient_id);
    combined.set(
      summary.recipient_id,
      existing
        ? {
            ...existing,
            cycle_total_cents: existing.cycle_total_cents + summary.cycle_total_cents,
            most_recent_donation_date: laterDate(
              existing.most_recent_donation_date,
              summary.most_recent_donation_date
            ),
          }
        : summary
    );
  }
  combined.forEach(summary => {
    state.data[summary.recipient_id] = summary;
  });
};

export const contributionSummarySliceReducer = createSlice({
  name: 'contributionSummary',
  initialState,
  reducers: {
    clear: () => initialState,
  },
  extraReducers: builder => {
    builder
      // Slate fetch: merge the requested recipients - don't touch the profile order.
      .addCase(getContributionSummaries.fulfilled, (state, action) => {
        state.isLoading = false;
        mergeSummaries(state, action.payload);
      })
      // All-recipients fetch: merge and record the server's order for the profile list.
      .addCase(getAllContributionSummaries.fulfilled, (state, action) => {
        state.isLoading = false;
        mergeSummaries(state, action.payload);
        // Dedupe: a collision means the same recipient_id can appear twice in
        // the payload. Set preserves first-occurrence order, matching the
        // server's cycle_total_cents DESC ordering and which row's identity
        // fields mergeSummaries keeps.
        state.orderedRecipientIds = Array.from(
          new Set(action.payload.map(summary => summary.recipient_id))
        );
      })
      .addMatcher(
        isAnyOf(getContributionSummaries.pending, getAllContributionSummaries.pending),
        state => {
          state.isLoading = true;
          state.error = null;
        }
      )
      .addMatcher(
        isAnyOf(getContributionSummaries.rejected, getAllContributionSummaries.rejected),
        (state, action) => {
          state.isLoading = false;
          state.error =
            action.payload ?? action.error.message ?? 'Failed to load contribution summaries';
        }
      );
  },
});

export const { clear: clearContributionSummaries } = contributionSummarySliceReducer.actions;
export default contributionSummarySliceReducer.reducer;
